/**
 * searchClient.js
 *
 * Tüm backend API iletişimi bu dosyada toplanır.
 * SOLID - Single Responsibility: Sadece HTTP isteklerinden sorumludur.
 * SOLID - Open/Closed: Yeni kaynak (ör. SEDIA adapter) eklemek için
 *         sadece bu dosyaya yeni fonksiyon eklenir, diğer kodlar değişmez.
 *
 * Frontend yalnızca /smart endpoint'ini kullanır.
 * Hangi motorun seçildiği response'daki `engine` alanından okunur.
 */

import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || window.location.origin;

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 
    "Content-Type": "application/json",
    "X-API-Key": process.env.REACT_APP_FRONTEND_API_KEY || "dev_secret_key_12345"
  },
  timeout: 30000,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Semantic Router üzerinden akıllı arama yapar.
 * Backend otomatik olarak FAISS ya da pgvector'ı seçer.
 *
 * @param {string} query  - Kullanıcının Türkçe arama sorgusu
 * @param {number} topK   - Kaç sonuç isteniyor (1-20, varsayılan 5)
 * @returns {{ data: object|null, error: string|null }}
 */
export async function search(query, topK = 5) {
  try {
    const response = await apiClient.post("/api/v1/search/smart", {
      query,
      top_k: topK,
    });
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: buildErrorMessage(err) };
  }
}

/**
 * RAG kullanarak arama yapar.
 * Backend'deki /rag endpoint'ini çağırır.
 *
 * @param {string} query  - Kullanıcının Türkçe arama sorgusu
 * @param {number} topK   - Kaç sonuç isteniyor (1-20, varsayılan 5)
 * @returns {{ data: object|null, error: string|null }}
 */
export async function ragSearch(query, topK = 5) {
  try {
    const response = await apiClient.post("/api/v1/search/rag", {
      query,
      top_k: topK,
    });
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: buildErrorMessage(err) };
  }
}

/**
 * Backend sağlık kontrolü.
 * @returns {boolean}
 */
export async function checkHealth() {
  try {
    await apiClient.get("/health");
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Yardımcı
// ---------------------------------------------------------------------------

function buildErrorMessage(err) {
  if (!err.response) {
    return "Backend'e bağlanılamadı. Docker container'ların çalıştığından emin olun.";
  }
  const { status, data } = err.response;
  if (status === 401) return "Yetkisiz istek: API Anahtarı eksik veya hatalı.";
  if (status === 422) return "Geçersiz istek: Arama metni en az 1 karakter olmalıdır.";
  if (status === 429) return "Çok fazla istek gönderdiniz (Rate Limit). Lütfen biraz bekleyin.";
  if (status === 503) return "FAISS index henüz oluşturulmamış. Önce /index/build endpoint'ini çalıştırın.";
  if (status === 500) return data?.detail || "Sunucu hatası oluştu. Backend loglarını kontrol edin.";
  return data?.detail || `Beklenmedik hata (HTTP ${status}).`;
}
